import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetJournalEntryDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  journalEntryId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the journal entry detail page.
 *
 * Fetches the journal entry in a single HTTP round trip on client-side
 * navigation. Throws `notFound()` (not a bare Response) when the entry is
 * missing — thrown Responses can't be serialized by the server RPC layer
 * (Seroval crash → 500) and aren't rejected properly on the client, which
 * left the loader hanging on a blank screen with the progress bar stuck.
 * `notFound()` renders the route's `notFoundComponent` with a clean 404.
 */
export const getJournalEntryDetailPageData = createServerFn({
  method: "GET",
})
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetJournalEntryDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("journalEntryDetail.pageData", async () => {
      const { getJournalEntryForUser } = await import("./journal-entry.server");
      const journalEntry = await getJournalEntryForUser(
        context.user.id,
        data.businessId,
        data.journalEntryId,
      );

      if (!journalEntry) {
        throw notFound();
      }

      return { journalEntry } as const;
    });
  });
